import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import SVGIcon from '../../../../assets/images/svg/SVGIcon';
import AppLayout from '../../../../component/appLayout';
import {
  BILLABLE_HOURS,
  BUTTON_TYPE,
  DISPLAY_HOURS,
  displayHoursOptions,
  ICON_SIZE
} from '../../../../global/constants';
import { getDateByThisMonth, getFormattedDate, getUrlParam, isEmpty } from '../../../../helpers/common';
import { ROUTES } from '../../../../routes/urls';
import { getIndividualCustomerDetail } from '../../../../services/customerServices';
import {
  getCustomerInvoiceData,
  getCustomerInvoiceTimeframes,
  updateTrackTimeInvoiceDetails
} from '../../../../services/invoiceService';
import {
  Group,
  HeaderBlock,
  Icon,
  HeaderTitle,
  Group1,
  MainGroup,
  Quantity,
  Price,
  Label,
  Text,
  Check,
  Title,
  Group2,
  Desc,
  BillingTime,
  BillingHour,
  LeftBilling,
  Buttons,
  FormView,
  TimeEntry,
  TimeDetail,
  StyledTable,
  TableRow,
  TableCell,
  TableHeaderCell,
  BillableHoursWrapper,
  DropdownDiv,
  RadioWrapper
} from './styles';
import Button from '../../../../component/button';
import { captureException } from '../../../../services/logService';
import { Dropdown, DropdownItem } from '../../../../component/dropdown';
import RadioButton from '../../../../component/radioButton';
import {
  CustomerBillableHoursDetailInterface,
  CustomerInvoiceTimeframesInterface
} from '../../../../interfaces/InvoiceInterface';
import BottomSubNavigation from '../../../../component/bottomNavigation/subNavigation';
import ResponsiveHeader from '../../../../component/responsiveHeader';
import { useMobileDevice } from '../../../../global/useMobile';
import { OuterDiv } from '../styles';
import { rootStore } from '../../../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import Input from '../../../../component/input';
import CheckboxInput from '../../../../component/input/checkboxInput';

const AddNewInvoiceModal: React.FC = () => {
  //States
  const [isSelectall, setIsSelectall] = useState(true);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [customerInvoiceTimeframes, setCustomerInvoiceTimeframes] = useState<CustomerInvoiceTimeframesInterface[]>();

  //Mobx state variables
  const { workspaceStore, invoiceStore } = rootStore;
  const { customerInvoiceData, inputInvoiceDetail, customerBillableHoursDetail } = invoiceStore;
  const { currentWorkspace: workspace } = workspaceStore;
  const { id: workspace_id } = workspace || {};

  //Other variables
  const history = useHistory();
  const customerId = getUrlParam(history.location.search, 'customer');
  const isMobile = useMobileDevice();

  // load initial data
  const loadData = useCallback(async () => {
    if (!isEmpty(workspace_id) && customerId) {
      const response = await Promise.all([
        getCustomerInvoiceData(customerId),
        getCustomerInvoiceTimeframes(),
        getIndividualCustomerDetail(customerId)
      ]);
      setCustomerInvoiceTimeframes(response[1]);
    }
  }, [workspace_id, customerId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    return () => {
      rootStore.invoiceStore.clearBillableHoursDetail();
    };
  }, []);

  // manage input values
  const onChangeInputEntryValue = useCallback((propsName: string, value: boolean) => {
    rootStore.invoiceStore.updateBillableHoursTimeEntry(propsName, value);
  }, []);

  // update assign value
  const onChangeAssign = useCallback(
    async (item: string, value: boolean) => {
      let updatedUsersList;
      updatedUsersList = customerInvoiceData?.map((ele) => {
        return ele?.id === item ? { ...ele, checked: value } : ele;
      });
      const isAllUsersSelected = updatedUsersList.every((user: { checked: boolean }) => user.checked);
      setIsSelectall(isAllUsersSelected);
      const filterUser = updatedUsersList?.filter((item: { checked: boolean }) => item?.checked);
      rootStore.invoiceStore.setCustomerInvoiceData(updatedUsersList);
      const usersFilter = filterUser?.map((item: { id: string }) => {
        return item?.id;
      });
      rootStore.invoiceStore.updateBillableHoursDetail('projects', usersFilter);
    },
    [customerInvoiceData]
  );

  // select user based on checkbox
  const onChangeSelectAll = useCallback(
    async (value: boolean) => {
      setIsSelectall(value);
      const updatedList = customerInvoiceData?.map((ele) => {
        return { ...ele, checked: value };
      });
      const filterUser = updatedList?.filter((item: { checked: boolean }) => item?.checked);
      rootStore.invoiceStore.setCustomerInvoiceData(updatedList);
      const usersFilter = filterUser?.map((item: { id: string }) => {
        return item?.id;
      });
      rootStore.invoiceStore.updateBillableHoursDetail('projects', usersFilter);
    },
    [customerInvoiceData]
  );

  // handle review invoice button click
  const onButtonClick = useCallback(async () => {
    try {
      setLoading(true);
      const payload: CustomerBillableHoursDetailInterface = {
        ...customerBillableHoursDetail,
        billableHoursTimeframe: customerBillableHoursDetail?.billableHoursTimeframe?.text
          ? customerBillableHoursDetail?.billableHoursTimeframe
          : { text: getDateByThisMonth(), value: getDateByThisMonth() },
        projects: customerBillableHoursDetail?.projects || [],
        customerId: customerId || ''
      };
      if (customerBillableHoursDetail?.billableHoursType !== BILLABLE_HOURS.INCLUDE_UNINVOICES_BILLABLE_HOURS_FROM) {
        delete (payload as { billableHoursTimeframe?: unknown }).billableHoursTimeframe;
      }
      if (
        customerBillableHoursDetail?.displayHoursType !== DISPLAY_HOURS.DETAILED ||
        customerBillableHoursDetail?.billableHoursType === BILLABLE_HOURS.DO_NOT_INCLUDE_ANY_HOURS
      ) {
        delete (payload as { displayHoursOption?: unknown }).displayHoursOption;
      }
      if (customerBillableHoursDetail?.billableHoursType === BILLABLE_HOURS.DO_NOT_INCLUDE_ANY_HOURS) {
        delete (payload as { displayHoursType?: unknown }).displayHoursType;
      }
      const response = await updateTrackTimeInvoiceDetails(payload);
      if (response) {
        rootStore.invoiceStore.clearBillableHoursDetail();
        history.push(`${ROUTES.ADD_NEW_INVOICES_2}?customer=${customerId}`);
      }
    } catch (error) {
      captureException(error);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  }, [customerBillableHoursDetail, customerId, history]);

  // handle cancel button click
  const onClickCancel = useCallback(() => {
    history.goBack();
  }, [history]);

  // handle radio button input value change
  const onChangeRadioInputValue = useCallback((propsName: string, value: number) => {
    rootStore.invoiceStore.updateBillableHoursDetail(propsName, value);
  }, []);

  // Handle timeframe selection
  const handleTimeframeSelect = useCallback((item: CustomerInvoiceTimeframesInterface) => {
    rootStore.invoiceStore.updateBillableHoursDetail('billableHoursTimeframe', item);
    setIsDropdownOpen(false);
  }, []);

  // render customer invoice data
  const renderCustomerInvoiceData = useMemo(() => {
    return customerInvoiceData?.map((item, index: number) => (
      <>
        <TableRow key={item?.id}>
          <TableCell>
            <Check>
              <CheckboxInput checked={item?.checked} onChange={(e) => onChangeAssign(item?.id, e.target.checked)} />
              <Label>{item?.name}</Label>
            </Check>
          </TableCell>
          <TableCell>{item?.unInvoicedHours}</TableCell>
          {!isEmpty(item?.lastInvoice) ? (
            <TableCell>
              {getFormattedDate(new Date(item?.lastInvoice?.date))} • ${item?.lastInvoice?.amount} •
              {item?.lastInvoice?.number}
            </TableCell>
          ) : (
            <TableCell>N/A</TableCell>
          )}
        </TableRow>
      </>
    ));
  }, [customerInvoiceData, onChangeAssign]);
  return (
    <>
      <AppLayout>
        <OuterDiv className='desktop-content'>
          <Group>
            {isMobile ? (
              <ResponsiveHeader title={inputInvoiceDetail?.customer?.companyName || ''} />
            ) : (
              <HeaderBlock>
                <Icon>
                  <SVGIcon
                    name='invoice-icon'
                    width={ICON_SIZE.XXXL}
                    height={ICON_SIZE.XXXL}
                    viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                    fill='var(--brand-primary)'
                  />
                </Icon>
                <HeaderTitle>Invoice for {inputInvoiceDetail?.customer?.companyName}</HeaderTitle>
              </HeaderBlock>
            )}
            <MainGroup>
              <Group1>
                <StyledTable>
                  <thead>
                    <tr>
                      <TableHeaderCell>
                        <Check>
                          <CheckboxInput checked={isSelectall} onChange={() => onChangeSelectAll(!isSelectall)} />
                          <Text>SELECT</Text>
                        </Check>
                      </TableHeaderCell>
                      <TableHeaderCell>
                        <Quantity>Uninvoiced Hours</Quantity>
                      </TableHeaderCell>
                      <TableHeaderCell>
                        <Price>Last Invoice</Price>
                      </TableHeaderCell>
                    </tr>
                  </thead>
                  <tbody>{renderCustomerInvoiceData}</tbody>
                </StyledTable>
              </Group1>

              <Group2>
                <Title>For Time & Materials projects, what hours would you like to invoice?</Title>
                <Desc>
                  <BillingTime>
                    <LeftBilling>
                      <BillingHour>Billable Hours</BillingHour>
                      <BillableHoursWrapper>
                        <RadioButton
                          id='all-uninvoiced-billable'
                          name='billableHoursType'
                          value={BILLABLE_HOURS.ALL_UNINVOICED_BILLABLE_HOURS}
                          checked={
                            customerBillableHoursDetail?.billableHoursType ===
                            BILLABLE_HOURS.ALL_UNINVOICED_BILLABLE_HOURS
                          }
                          label='All uninvoiced billable hours'
                          onChange={(value) => onChangeRadioInputValue('billableHoursType', Number(value))}
                          className='radio-Button'
                        />
                        <DropdownDiv>
                          <RadioButton
                            id='include-uninvoiced-from'
                            name='billableHoursType'
                            value={BILLABLE_HOURS.INCLUDE_UNINVOICES_BILLABLE_HOURS_FROM}
                            checked={
                              customerBillableHoursDetail?.billableHoursType ===
                              BILLABLE_HOURS.INCLUDE_UNINVOICES_BILLABLE_HOURS_FROM
                            }
                            label='Include uninvoiced billable hours from'
                            onChange={(value) => onChangeRadioInputValue('billableHoursType', Number(value))}
                            className='radio-Button'
                          />
                          <FormView>
                            <Dropdown
                              modalTitle='Billable Hours from'
                              isMinWidth={1}
                              onOutsideClick={() => setIsDropdownOpen(false)}
                              content={
                                <Input
                                  value={
                                    !isEmpty(customerBillableHoursDetail?.billableHoursTimeframe?.value)
                                      ? customerBillableHoursDetail?.billableHoursTimeframe?.value
                                      : customerInvoiceTimeframes?.[0]?.value
                                  }
                                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                  trailingIconName='down-arrow-icon'
                                  trailingIconClassName={`angleDown-icon ${isDropdownOpen ? 'rotate' : ''}`}
                                />
                              }
                              trigger='click'>
                              <>
                                {customerInvoiceTimeframes?.map((item) => (
                                  <DropdownItem
                                    key={item.value}
                                    label={item.value}
                                    onClick={() => handleTimeframeSelect(item)}
                                  />
                                ))}
                              </>
                            </Dropdown>
                          </FormView>
                        </DropdownDiv>
                        <RadioButton
                          id='no-hours'
                          name='billableHoursType'
                          value={BILLABLE_HOURS.DO_NOT_INCLUDE_ANY_HOURS}
                          checked={
                            customerBillableHoursDetail?.billableHoursType === BILLABLE_HOURS.DO_NOT_INCLUDE_ANY_HOURS
                          }
                          label='Do not include any hours'
                          onChange={(value) => onChangeRadioInputValue('billableHoursType', Number(value))}
                          className='radio-Button'
                        />
                      </BillableHoursWrapper>
                    </LeftBilling>
                  </BillingTime>
                  {customerBillableHoursDetail?.billableHoursType !== BILLABLE_HOURS.DO_NOT_INCLUDE_ANY_HOURS && (
                    <LeftBilling>
                      <BillingHour>How to Display Hours</BillingHour>
                      <RadioWrapper>
                        {displayHoursOptions.map((option) => (
                          <RadioButton
                            key={option.id}
                            id={option.id}
                            name='displayHoursType'
                            value={option.value}
                            checked={customerBillableHoursDetail?.displayHoursType === option.value}
                            label={option.label}
                            onChange={(value) => onChangeRadioInputValue('displayHoursType', Number(value))}
                            className='radio-Button'
                          />
                        ))}
                      </RadioWrapper>
                    </LeftBilling>
                  )}
                  {customerBillableHoursDetail?.displayHoursType === DISPLAY_HOURS.DETAILED &&
                    customerBillableHoursDetail?.billableHoursType !== BILLABLE_HOURS.DO_NOT_INCLUDE_ANY_HOURS && (
                      <LeftBilling>
                        <TimeDetail>
                          <BillingHour>Time Entry Details</BillingHour>
                          <TimeEntry>Include the following info for each line item:</TimeEntry>
                        </TimeDetail>
                        <Check>
                          <CheckboxInput
                            defaultChecked={true}
                            checked={customerBillableHoursDetail?.displayHoursOption?.project}
                            onChange={(e) =>
                              onChangeInputEntryValue(
                                'project',
                                !customerBillableHoursDetail?.displayHoursOption?.project
                              )
                            }
                          />
                          <Label>Project</Label>
                        </Check>
                        <Check>
                          <CheckboxInput
                            defaultChecked={true}
                            checked={customerBillableHoursDetail?.displayHoursOption?.task}
                            onChange={(e) =>
                              onChangeInputEntryValue('task', !customerBillableHoursDetail?.displayHoursOption?.task)
                            }
                          />
                          <Label>Task</Label>
                        </Check>
                        <Check>
                          <CheckboxInput
                            defaultChecked={true}
                            checked={customerBillableHoursDetail?.displayHoursOption?.people}
                            onChange={(e) =>
                              onChangeInputEntryValue(
                                'people',
                                !customerBillableHoursDetail?.displayHoursOption?.people
                              )
                            }
                          />
                          <Label>People</Label>
                        </Check>
                        <Check>
                          <CheckboxInput
                            defaultChecked={true}
                            checked={customerBillableHoursDetail?.displayHoursOption?.date}
                            onChange={(e) =>
                              onChangeInputEntryValue('date', !customerBillableHoursDetail?.displayHoursOption?.date)
                            }
                          />
                          <Label>Date</Label>
                        </Check>
                      </LeftBilling>
                    )}
                </Desc>
              </Group2>
              <Buttons>
                <Button
                  title='Review Invoice'
                  variant={BUTTON_TYPE.PRIMARY}
                  onClick={onButtonClick}
                  isLoading={loading}
                  disabled={loading}
                />
                <Button title='Cancel' variant={BUTTON_TYPE.SECONDARY} onClick={onClickCancel} />
              </Buttons>
            </MainGroup>
          </Group>
        </OuterDiv>
      </AppLayout>
      <BottomSubNavigation />
    </>
  );
};

export default observer(AddNewInvoiceModal);
